import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Flame, Trophy, Plus, Trash2, Check, Award, Loader2, CheckCircle2, Zap, Target,
} from 'lucide-react';
import { ICONS, metricIcon, unitForMetric, fmtNum, ACCENT_FALLBACK } from '@/lib/gamify';

const METRICS = ['sessions', 'analyses', 'time_on_water', 'distance', 'spots', 'streak'];
const PERIODS = ['week', 'month', 'year', 'all', 'custom'];

const RewardChip = ({ goal }) => {
  const { t } = useTranslation();
  if (goal.reward_credits > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#00B4D8]/12 text-[#48CAE4] border border-[#00B4D8]/25" data-testid="reward-credits">
        <Zap className="w-3 h-3" /> {t('gamify.goals.rewardCredits', { count: goal.reward_credits })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#FFB020]/12 text-[#FFB020] border border-[#FFB020]/25" data-testid="reward-badge">
      <Award className="w-3 h-3" /> {t('gamify.goals.badgeOnly')}
    </span>
  );
};

const GoalCard = ({ goal, onDelete }) => {
  const { t } = useTranslation();
  const Icon = metricIcon(goal.metric);
  const accent = goal.accent || ACCENT_FALLBACK;
  const unit = t(`gamify.units.${unitForMetric(goal.metric)}`);
  const completed = goal.status === 'completed';
  const label = goal.template
    ? t(`gamify.templates.${goal.template}.title`)
    : (goal.title || t(`gamify.metrics.${goal.metric}`));

  return (
    <div className={`kite-card p-5 ${completed ? 'opacity-90' : ''}`} data-testid={`goal-card-${goal.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1f` }}>
            <Icon className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate">{label}</div>
            <div className="text-xs text-muted-foreground">{t(`gamify.periods.${goal.period}`)}</div>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="text-muted-foreground hover:text-[#FF6B35] transition-colors p-1" data-testid={`goal-delete-${goal.id}`} aria-label={t('gamify.goals.delete')}>
              <Trash2 className="w-4 h-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#12161B] border-white/15 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('gamify.goals.deleteConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground">{t('gamify.goals.deleteConfirmDesc')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/10">{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="bg-[#FF6B35] hover:bg-[#FFB020] text-black" onClick={() => onDelete(goal.id)} data-testid={`goal-delete-confirm-${goal.id}`}>{t('gamify.goals.delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-data text-sm" style={{ color: accent }}>{fmtNum(goal.current)} <span className="text-muted-foreground">/ {fmtNum(goal.target)} {unit}</span></span>
          <span className="font-data text-xs text-muted-foreground">{goal.pct}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${goal.pct}%`, background: accent, transition: 'width 300ms ease' }} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <RewardChip goal={goal} />
        {completed && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#2EE59D]/12 text-[#2EE59D] border border-[#2EE59D]/25" data-testid="goal-completed-badge">
            <CheckCircle2 className="w-3 h-3" /> {t('gamify.goals.reached')}
          </span>
        )}
      </div>
    </div>
  );
};

const CustomGoalDialog = ({ onCreated }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState('sessions');
  const [target, setTarget] = useState('');
  const [period, setPeriod] = useState('month');
  const [title, setTitle] = useState('');
  const [cstart, setCstart] = useState('');
  const [cend, setCend] = useState('');
  const [saving, setSaving] = useState(false);

  const effPeriod = metric === 'streak' ? 'all' : period;

  const submit = async () => {
    const num = parseFloat(target);
    if (!num || num <= 0) { toast.error(t('gamify.goals.target')); return; }
    setSaving(true);
    try {
      const payload = { metric, target: num, period: effPeriod, title: title || undefined };
      if (effPeriod === 'custom') { payload.custom_start = cstart || undefined; payload.custom_end = cend || undefined; }
      const { data } = await api.post('/gamification/goals', payload);
      toast.success(t('gamify.toast.created'));
      setOpen(false);
      setTarget(''); setTitle('');
      onCreated(data);
    } catch (e) {
      toast.error(apiError(e, t('gamify.toast.createError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="new-custom-goal-btn">
          <Plus className="w-4 h-4 mr-1" /> {t('gamify.goals.add')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#12161B] border-white/15 text-white">
        <DialogHeader>
          <DialogTitle>{t('gamify.goals.custom')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{t('gamify.goals.customDesc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">{t('gamify.goals.metric')}</Label>
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="custom-metric-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12161B] border-white/15 text-white">
                {METRICS.map((m) => <SelectItem key={m} value={m} className="focus:bg-white/10">{t(`gamify.metrics.${m}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">{t('gamify.goals.target')} <span className="text-muted-foreground">({t(`gamify.units.${unitForMetric(metric)}`)})</span></Label>
              <Input type="number" min="1" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="custom-target-input" />
            </div>
            <div>
              <Label className="text-sm">{t('gamify.goals.period')}</Label>
              <Select value={effPeriod} onValueChange={setPeriod} disabled={metric === 'streak'}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="custom-period-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12161B] border-white/15 text-white">
                  {PERIODS.map((p) => <SelectItem key={p} value={p} className="focus:bg-white/10">{t(`gamify.periods.${p}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {effPeriod === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">{t('gamify.goals.start')}</Label>
                <Input type="date" value={cstart} onChange={(e) => setCstart(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="custom-start-input" />
              </div>
              <div>
                <Label className="text-sm">{t('gamify.goals.end')}</Label>
                <Input type="date" value={cend} onChange={(e) => setCend(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="custom-end-input" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-sm">{t('gamify.goals.titleLabel')}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('gamify.goals.titlePlaceholder')} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="custom-title-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold w-full sm:w-auto" data-testid="custom-goal-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('gamify.goals.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplateCard = ({ tpl, active, onAdd, adding }) => {
  const { t } = useTranslation();
  const Icon = ICONS[tpl.icon] || Target;
  const accent = tpl.accent || ACCENT_FALLBACK;
  return (
    <div className="kite-card p-5 flex flex-col" data-testid={`template-${tpl.key}`}>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1f` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="font-semibold">{t(`gamify.templates.${tpl.key}.title`)}</div>
      </div>
      <p className="text-sm text-muted-foreground mt-2 flex-1">{t(`gamify.templates.${tpl.key}.desc`)}</p>
      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#00B4D8]/12 text-[#48CAE4] border border-[#00B4D8]/25">
          <Zap className="w-3 h-3" /> {t('gamify.goals.rewardCredits', { count: tpl.reward_credits })}
        </span>
        {active ? (
          <span className="inline-flex items-center gap-1 text-xs text-[#2EE59D] font-medium" data-testid={`template-active-${tpl.key}`}>
            <Check className="w-4 h-4" /> {t('gamify.goals.active')}
          </span>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onAdd(tpl.key)} disabled={adding}
            className="border-white/15 hover:bg-white/10 rounded-lg" data-testid={`template-add-${tpl.key}`}>
            <Plus className="w-4 h-4 mr-1" /> {t('gamify.goals.add')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default function Goals() {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [streak, setStreak] = useState(null);
  const [badges, setBadges] = useState([]);
  const [goals, setGoals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingKey, setAddingKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await api.get('/gamification/summary');
      setStreak(s.data.streak);
      setBadges(s.data.badges || []);
      if (s.data.recent_completions?.length) {
        s.data.recent_completions.forEach((c) => {
          const name = c.template ? t(`gamify.templates.${c.template}.title`) : (c.title || t(`gamify.metrics.${c.metric}`));
          if (c.reward_credits > 0) toast.success(t('gamify.toast.completedReward', { name, credits: c.reward_credits }));
          else toast.success(t('gamify.toast.completed', { name }));
        });
        refreshUser();
      }
      const [g, tpl] = await Promise.all([
        api.get('/gamification/goals'),
        api.get('/gamification/goals/templates'),
      ]);
      setGoals(g.data);
      setTemplates(tpl.data.templates || []);
    } catch (e) {
      toast.error(apiError(e, t('gamify.toast.loadError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const addTemplate = async (key) => {
    setAddingKey(key);
    try {
      await api.post('/gamification/goals', { template: key });
      toast.success(t('gamify.toast.created'));
      await load();
    } catch (e) {
      toast.error(apiError(e, t('gamify.toast.createError')));
    } finally {
      setAddingKey(null);
    }
  };

  const removeGoal = async (id) => {
    try {
      await api.delete(`/gamification/goals/${id}`);
      toast.success(t('gamify.toast.deleted'));
      setGoals((prev) => prev.filter((g) => g.id !== id));
    } catch (e) {
      toast.error(apiError(e, t('gamify.toast.deleteError')));
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" /></div>;
  }

  const activeGoals = goals.filter((g) => g.status === 'active');
  const completedGoals = goals.filter((g) => g.status === 'completed');
  const activeTemplateKeys = new Set(activeGoals.map((g) => g.template).filter(Boolean));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="goals-page">
      <div className="label-eyebrow text-[#48CAE4] mb-2">{t('gamify.eyebrow')}</div>
      <h1 className="font-display text-4xl sm:text-5xl">{t('gamify.title')}</h1>
      <p className="text-muted-foreground mt-1 max-w-2xl">{t('gamify.subtitle')}</p>

      {/* Streak + badges */}
      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 kite-card p-6" data-testid="streak-hero">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#FF6B35]/15 flex items-center justify-center shrink-0">
                <Flame className="w-7 h-7 text-[#FF6B35]" />
              </div>
              <div>
                <div className="label-eyebrow text-muted-foreground">{t('gamify.streak.title')}</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-data text-4xl font-semibold text-white" data-testid="hero-streak-current">{streak?.current ?? 0}</span>
                  <span className="text-muted-foreground">{t('gamify.streak.weeks', { count: streak?.current ?? 0 })}</span>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Trophy className="w-3 h-3 text-[#FFB020]" /> {t('gamify.streak.best')}: <span className="text-white font-data">{streak?.best ?? 0}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2">
              <span className="label-eyebrow text-muted-foreground">{t('gamify.streak.last8')}</span>
              <div className="flex items-center gap-1.5">
                {(streak?.weeks || []).map((w) => (
                  <span key={w.week_start} title={w.week_start} className="h-3.5 w-3.5 rounded-[4px]"
                    style={{ background: w.active ? '#FF6B35' : 'rgba(255,255,255,0.10)' }} data-active={w.active ? '1' : '0'} />
                ))}
              </div>
            </div>
          </div>
          <p className="text-sm mt-4 text-muted-foreground">
            {streak?.this_week_active ? t('gamify.streak.active') : (streak?.current > 0 ? t('gamify.streak.inactive') : t('gamify.streak.start'))}
          </p>
        </div>

        <div className="kite-card p-6" data-testid="badges-card">
          <h3 className="font-semibold flex items-center gap-2 mb-3"><Award className="w-4 h-4 text-[#FFB020]" /> {t('gamify.badgesTitle')}</h3>
          {badges.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('gamify.badgesEmpty')}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {badges.map((b) => (
                <span key={b.key} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#0E1114] border border-white/10" data-testid={`badge-${b.key}`}>
                  <Award className="w-3.5 h-3.5 text-[#FFB020]" /> {t(`gamify.badges.${b.key}`, b.key)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Active goals */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-3xl">{t('gamify.goals.active')}</h2>
          <CustomGoalDialog onCreated={load} />
        </div>
        {activeGoals.length === 0 ? (
          <div className="kite-card p-10 text-center" data-testid="active-goals-empty">
            <div className="w-14 h-14 rounded-2xl bg-[#00B4D8]/12 flex items-center justify-center mx-auto"><Target className="w-7 h-7 text-[#00B4D8]" /></div>
            <p className="text-muted-foreground mt-4 max-w-md mx-auto">{t('gamify.goals.empty')}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeGoals.map((g) => <GoalCard key={g.id} goal={g} onDelete={removeGoal} />)}
          </div>
        )}
      </div>

      {/* Quick goals / templates */}
      <div className="mt-10">
        <h2 className="font-display text-3xl mb-4">{t('gamify.goals.fromTemplate')}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tpl) => (
            <TemplateCard key={tpl.key} tpl={tpl} active={activeTemplateKeys.has(tpl.key)} onAdd={addTemplate} adding={addingKey === tpl.key} />
          ))}
        </div>
      </div>

      {/* Completed */}
      {completedGoals.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-3xl mb-4">{t('gamify.goals.completed')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedGoals.map((g) => <GoalCard key={g.id} goal={g} onDelete={removeGoal} />)}
          </div>
        </div>
      )}
    </div>
  );
}
